import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker from '../components/SchoolPicker';
import apiService from '../services/api';
import '../styles/StaffPages.css';

// One flex row per prize with fixed column widths, rather than .form-row — that
// is a two-column grid, so a four-part row wrapped into a 2x2 block.
const PRIZE_ROW = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' };
const COL_POINTS = { width: '120px', flex: '0 0 auto' };
const COL_WEIGHT = { width: '120px', flex: '0 0 auto' };
const COL_CHANCE = { width: '70px', flex: '0 0 auto', fontFamily: '"Baikal Book"', fontSize: '14px', color: '#374151' };
// Matches .form-label, so the column headers read the same as field labels do
// elsewhere in the console.
const COL_HEADING = {
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  fontWeight: 100,
  color: '#000',
  opacity: 0.5,
};

const messageStyle = (kind) => ({
  marginTop: '16px',
  padding: '10px 14px',
  backgroundColor: kind === 'error' ? '#fee2e2' : '#d1fae5',
  border: `1px solid ${kind === 'error' ? '#fecaca' : '#a7f3d0'}`,
  borderRadius: '8px',
  color: kind === 'error' ? '#991b1b' : '#065f46',
  fontFamily: '"Baikal Book"',
  fontSize: '13px',
});

/**
 * Daily Card Draw settings, per school.
 *
 * Schools opt in — a school with no saved config reads back disabled with an
 * empty prize list, and an empty list means the draw falls back to the server's
 * defaults rather than awarding nothing.
 */
const SuperuserCardDrawManagement = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [prizes, setPrizes] = useState([]);
  const [defaultPrizes, setDefaultPrizes] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        const list = await apiService.getSchools();
        const rows = Array.isArray(list) ? list : list?.results || [];
        setSchools(rows);
      } catch (err) {
        console.error('Failed to load card draw page:', err);
        setError('Could not load schools.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const loadConfig = useCallback(async (id) => {
    if (!id) return;
    setError('');
    setNotice('');
    try {
      const data = await apiService.getCardDrawConfig(id);
      setEnabled(!!data.enabled);
      setPrizes(Array.isArray(data.prizes) ? data.prizes : []);
      setDefaultPrizes(Array.isArray(data.default_prizes) ? data.default_prizes : []);
    } catch (err) {
      console.error('Failed to load card draw config:', err);
      setError('Could not load this school’s card draw settings.');
    }
  }, []);

  useEffect(() => {
    loadConfig(selectedSchool?.id);
  }, [selectedSchool, loadConfig]);

  const updatePrize = (index, field, value) => {
    setPrizes((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addPrize = () => setPrizes((rows) => [...rows, { points: '', weight: 1 }]);
  const removePrize = (index) => setPrizes((rows) => rows.filter((_, i) => i !== index));

  const handleSave = async () => {
    setError('');
    setNotice('');

    const cleaned = [];
    for (const row of prizes) {
      const points = parseInt(row.points, 10);
      const weight = parseInt(row.weight, 10);
      if (!Number.isFinite(points) || !Number.isFinite(weight) || points <= 0 || weight <= 0) {
        setError('Every prize needs points and a weight above zero.');
        return;
      }
      cleaned.push({ points, weight });
    }

    setSaving(true);
    try {
      const data = await apiService.updateCardDrawConfig(selectedSchool.id, {
        enabled,
        prizes: cleaned,
      });
      setEnabled(!!data.enabled);
      setPrizes(Array.isArray(data.prizes) ? data.prizes : []);
      setNotice('Saved.');
    } catch (err) {
      console.error('Failed to save card draw config:', err);
      setError(err?.response?.data?.error || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Weights are relative, so the odds only mean anything against their total.
  const totalWeight = prizes.reduce((sum, row) => {
    const weight = parseInt(row.weight, 10);
    return sum + (Number.isFinite(weight) && weight > 0 ? weight : 0);
  }, 0);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Card Draw Management</h1>
          <p className="staff-page-subheader">
            Turn the Daily Card Draw on for a school and set what it can award.
          </p>

          <div className="add-reward-section">
            <SchoolPicker schools={schools} value={selectedSchool} onChange={setSelectedSchool} />
            {!selectedSchool ? (
              <p className="staff-page-content" style={{ marginTop: '24px', opacity: 0.7 }}>
                Please select a school
              </p>
            ) : (
            <>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '24px',
                cursor: 'pointer',
                fontFamily: '"Baikal Book"',
                fontSize: '15px',
              }}
            >
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              Daily Card Draw enabled
            </label>
            <p className="staff-page-content" style={{ marginTop: '6px' }}>
              Off by default. While it is off, students get no draw and the card is hidden
              on their Rewards page.
            </p>

            <h2
              className="staff-section-header"
              style={{ marginTop: '40px', marginBottom: '8px' }}
            >
              Prizes
            </h2>
            <p className="staff-page-content" style={{ margin: '0 0 24px' }}>
              Weights are relative &mdash; weight 2 comes up twice as often as weight 1.
              {defaultPrizes.length > 0 && (
                <>
                  {' '}Leave empty to use the default{' '}
                  {defaultPrizes.map((p) => p.points).join(' / ')} points.
                </>
              )}
            </p>

            {prizes.length > 0 && (
              <div style={{ ...PRIZE_ROW, marginBottom: '8px' }}>
                <div style={{ ...COL_POINTS, ...COL_HEADING }}>Points</div>
                <div style={{ ...COL_WEIGHT, ...COL_HEADING }}>Weight</div>
                <div style={{ ...COL_CHANCE, ...COL_HEADING }}>Chance</div>
              </div>
            )}

            {prizes.map((row, index) => {
              const weight = parseInt(row.weight, 10);
              // Up to three decimals, trailing zeros trimmed: 25% stays 25%,
              // a three-way split reads 33.333%, and 1-in-8 reads 12.5% rather
              // than 12.500%.
              const pct =
                totalWeight > 0 && Number.isFinite(weight) && weight > 0
                  ? (weight / totalWeight) * 100
                  : null;
              const chance =
                pct === null ? '—' : `${parseFloat(pct.toFixed(3))}%`;
              return (
                <div style={PRIZE_ROW} key={index}>
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    style={COL_POINTS}
                    value={row.points}
                    onChange={(e) => updatePrize(index, 'points', e.target.value)}
                  />
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    style={COL_WEIGHT}
                    value={row.weight}
                    onChange={(e) => updatePrize(index, 'weight', e.target.value)}
                  />
                  <div style={COL_CHANCE}>{chance}</div>
                  <button
                    type="button"
                    className="staff-page-button"
                    onClick={() => removePrize(index)}
                  >
                    <span className="staff-page-button-text">Remove</span>
                  </button>
                </div>
              );
            })}

            <div style={{ marginTop: '20px' }}>
              <button type="button" className="staff-page-button" onClick={addPrize}>
                <span className="staff-page-button-text">Add prize</span>
              </button>
            </div>

          <div style={{ marginTop: '24px' }}>
            <button
              type="button"
              className="staff-page-button"
              onClick={handleSave}
              disabled={saving || !selectedSchool}
            >
              <span className="staff-page-button-text">
                {saving ? 'Saving...' : 'Save changes'}
              </span>
            </button>

            {error && <div style={messageStyle('error')}>{error}</div>}
            {notice && <div style={messageStyle('success')}>{notice}</div>}
          </div>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperuserCardDrawManagement;
